import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { api, ApiError, type Deployment, type EnvRow, type RuntimeStatusLive } from '../lib/api'
import { ENV_LABEL, formatBytes, formatRelative } from '../lib/format'
import { StatusBadge, Tag, Spinner, ErrorNote, InfoNote } from '../components/ui/Primitives'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { LiveConsole } from '../features/LiveConsole'
import { FileManager } from '../features/FileManager'
import { DatabaseTab } from '../features/SqlConsole'
import { BackupsTab } from '../features/BackupsTab'

const TABS = ['Resumen', 'Consola', 'Archivos', 'Base de datos', 'Backups', 'Entorno', 'Dominios', 'Ajustes'] as const
type Tab = (typeof TABS)[number]

export default function InstanceDetail() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('Resumen')
  const [pending, setPending] = useState<{ action: string; label: string } | null>(null)
  const [confirmName, setConfirmName] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const { data: d, isLoading, error } = useQuery({
    queryKey: ['deployment', id],
    queryFn: () => api.get<Deployment>(`/deployments/${id}`),
  })

  const { data: live } = useQuery({
    queryKey: ['deployment-status', id],
    queryFn: () => api.get<RuntimeStatusLive>(`/deployments/${id}/status`),
    refetchInterval: 10_000,
    enabled: Boolean(d),
  })

  const power = useMutation({
    mutationFn: (v: { action: string; confirmName?: string }) =>
      api.post(`/deployments/${id}/${v.action}`, v.confirmName ? { confirmName: v.confirmName } : {}),
    onSuccess: (_r, v) => {
      setPending(null)
      setConfirmName(null)
      setBanner({ kind: 'ok', text: `Acción "${v.action}" ejecutada.` })
      void qc.invalidateQueries({ queryKey: ['deployment', id] })
      void qc.invalidateQueries({ queryKey: ['deployment-status', id] })
      void qc.invalidateQueries({ queryKey: ['deployments'] })
    },
    onError: (err) => {
      // Si el backend pide confirmación, se abre el diálogo con el nombre
      // exacto que espera. La UI no adivina: usa lo que dijo el servidor.
      if (err instanceof ApiError && err.code === 'confirmation_required' && err.expectedName) {
        setConfirmName(err.expectedName)
        return
      }
      setPending(null)
      setBanner({ kind: 'err', text: err instanceof Error ? err.message : 'Falló la acción' })
    },
  })

  if (isLoading) return <div className="grid place-items-center py-20"><Spinner className="h-6 w-6" /></div>
  if (error || !d) return <ErrorNote>No se pudo cargar la instancia.</ErrorNote>

  const caps = new Set(d.capabilities ?? [])
  const canPower = caps.has('instance.power')

  function act(action: string, label: string) {
    setBanner(null)
    setPending({ action, label })
    power.mutate({ action })
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/" className="hint hover:text-slate-300">← Instancias</Link>
          <h1 className="mt-1 truncate text-xl font-semibold">{d.app.slug} / {d.name}</h1>
          <p className="hint mt-0.5">
            {ENV_LABEL[d.environment]} · {d.server.name} · {d.runtimeDriver.toLowerCase()}
            {d.systemdUnit && ` · ${d.systemdUnit}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={live?.status ?? d.status} />
          {d.protected && <Tag tone="amber">protegida</Tag>}
          {d.adopted && <Tag tone="purple">adoptada</Tag>}
        </div>
      </header>

      {banner && (
        banner.kind === 'ok'
          ? <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300">{banner.text}</div>
          : <ErrorNote>{banner.text}</ErrorNote>
      )}

      {d.adopted && (
        <InfoNote>
          Esta instancia fue <strong>adoptada</strong> de algo que ya estaba corriendo. El panel la
          controla y la observa, pero no cambia cómo arranca: eso sigue viviendo en
          {d.systemdUnit ? ` la unidad ${d.systemdUnit}` : d.composePath ? ` ${d.composePath}` : ' el servidor'}.
        </InfoNote>
      )}

      <div className="flex flex-wrap gap-2">
        {(['start', 'restart', 'stop'] as const).map((a) => (
          <button
            key={a}
            className={a === 'stop' ? 'btn-danger' : 'btn-ghost'}
            disabled={!canPower || power.isPending}
            onClick={() => act(a, a === 'start' ? 'Arrancar' : a === 'stop' ? 'Parar' : 'Reiniciar')}
            title={canPower ? undefined : 'No tenés el permiso instance.power sobre esta instancia'}
          >
            {power.isPending && pending?.action === a && <Spinner />}
            {a === 'start' ? 'Arrancar' : a === 'stop' ? 'Parar' : 'Reiniciar'}
          </button>
        ))}
      </div>

      <nav className="flex gap-1 overflow-x-auto border-b">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              'whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
              tab === t ? 'border-blue-500 font-medium text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-200',
            )}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === 'Resumen' && <Overview d={d} live={live} />}
      {tab === 'Consola' && <LiveConsole deploymentId={id} />}
      {tab === 'Archivos' && <FileManager id={id} caps={caps} />}
      {tab === 'Base de datos' && <DatabaseTab deploymentId={id} caps={caps} />}
      {tab === 'Backups' && (
        <BackupsTab deploymentId={id} caps={caps} isProtected={d.protected} instanceName={d.name} />
      )}
      {tab === 'Entorno' && <EnvTab id={id} caps={caps} />}
      {tab === 'Dominios' && <DomainsTab d={d} />}
      {tab === 'Ajustes' && <SettingsTab d={d} />}

      <ConfirmDialog
        open={Boolean(confirmName)}
        title={`Confirmar: ${pending?.label ?? 'acción'}`}
        description={`"${d.app.slug} / ${d.name}" está marcada como protegida, así que esta acción necesita confirmación explícita.`}
        confirmName={confirmName}
        confirmLabel={pending?.label ?? 'Confirmar'}
        busy={power.isPending}
        onCancel={() => { setConfirmName(null); setPending(null) }}
        onConfirm={(typed) => pending && power.mutate({ action: pending.action, confirmName: typed })}
      />
    </div>
  )
}

function Overview({ d, live }: { d: Deployment; live?: RuntimeStatusLive }) {
  const rows: Array<[string, string]> = [
    ['Estado', live?.detail ?? d.statusMessage ?? '—'],
    ['Runtime', d.runtimeDriver.toLowerCase()],
    ['Servidor', d.server.name],
    ['Directorio', d.workDir],
    ['Rama', d.branch],
    ['Puerto', d.port ? String(d.port) : '—'],
    ['PID', live?.pid ? String(live.pid) : '—'],
    ['Memoria', formatBytes(live?.memoryBytes)],
    ['CPU', live?.cpuPercent !== undefined ? `${live.cpuPercent.toFixed(1)} %` : '—'],
    ['Último despliegue', formatRelative(d.lastDeployedAt)],
    ['Comando de arranque', d.startCmd.length ? d.startCmd.join(' ') : '—'],
  ]
  return (
    <div className="card">
      <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 border-b border-slate-800/60 pb-2">
            <dt className="hint shrink-0">{k}</dt>
            <dd className="truncate text-right text-sm font-mono">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function EnvTab({ id, caps }: { id: string; caps: Set<string> }) {
  const [reveal, setReveal] = useState(false)
  const canReveal = caps.has('instance.env.reveal')
  const { data, isLoading } = useQuery({
    queryKey: ['env', id, reveal],
    queryFn: () => api.get<EnvRow[]>(`/deployments/${id}/env${reveal ? '?reveal=true' : ''}`),
  })

  if (isLoading) return <Spinner />

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button
          className="btn-ghost text-sm"
          disabled={!canReveal}
          onClick={() => setReveal((r) => !r)}
          title={canReveal ? undefined : 'Necesitás el permiso instance.env.reveal para ver los valores'}
        >
          {reveal ? 'Ocultar valores' : 'Revelar valores'}
        </button>
        {!canReveal && <span className="hint">Ves las claves, no los valores secretos.</span>}
      </div>

      <div className="card divide-y divide-slate-800/60 p-0">
        {(data ?? []).map((v) => (
          <div key={v.key} className="flex items-center gap-4 px-5 py-3">
            <code className="w-56 shrink-0 truncate font-mono text-sm">{v.key}</code>
            <code className={clsx('min-w-0 flex-1 truncate font-mono text-sm', v.masked ? 'text-slate-500' : 'text-slate-300')}>
              {v.value}
            </code>
            {v.isSecret && <Tag tone="amber">secreto</Tag>}
          </div>
        ))}
        {(data ?? []).length === 0 && <p className="hint px-5 py-6">Sin variables cargadas.</p>}
      </div>
    </div>
  )
}

function DomainsTab({ d }: { d: Deployment }) {
  const managed = d.server.proxyManaged
  return (
    <div className="space-y-3">
      {!managed && d.server.proxyDriver === 'NGINX' && (
        <InfoNote>
          El proxy de <strong>{d.server.name}</strong> es nginx y el panel lo está observando en
          solo lectura. Podés registrar dominios acá, pero hay que apuntarlos a mano hasta que
          marques ese servidor como gestionado.
        </InfoNote>
      )}
      <div className="card divide-y divide-slate-800/60 p-0">
        {d.domains.map((x) => (
          <div key={x.hostname} className="flex items-center justify-between px-5 py-3">
            <code className="font-mono text-sm">{x.hostname}{x.pathPrefix !== '/' && x.pathPrefix}</code>
            {d.port && <span className="hint">→ 127.0.0.1:{d.port}</span>}
          </div>
        ))}
        {d.domains.length === 0 && <p className="hint px-5 py-6">Sin dominios asignados.</p>}
      </div>
    </div>
  )
}

function SettingsTab({ d }: { d: Deployment }) {
  return (
    <div className="card space-y-4">
      <div>
        <p className="text-sm font-medium">Protección</p>
        <p className="hint mt-1">
          {d.protected
            ? 'Está protegida: toda acción destructiva pide escribir el nombre, y sus backups no se pueden borrar a mano.'
            : 'No está protegida. Las acciones destructivas se ejecutan con una confirmación simple.'}
        </p>
      </div>
      <div>
        <p className="text-sm font-medium">Origen</p>
        <p className="hint mt-1">
          {d.adopted
            ? 'Adoptada de algo que ya existía. El panel no reescribe su configuración de arranque.'
            : 'Creada desde el panel. El panel gestiona su ciclo de vida completo.'}
        </p>
      </div>
      <div>
        <p className="text-sm font-medium">Límites</p>
        <p className="hint mt-1">
          CPU: {d.cpuLimit ?? 'sin límite'} · Memoria: {d.memoryLimitMb ? `${d.memoryLimitMb} MB` : 'sin límite'}
        </p>
      </div>
    </div>
  )
}
